import { ScopeRulesEditor } from '@/components/admin/ScopeRulesEditor';
import { ColumnTable } from '@/components/admin/data-sources/ColumnTable';
import { DescriptionEditor } from '@/components/admin/data-sources/DescriptionEditor';
import { DuckdbTableauSourcePanel } from '@/components/admin/data-sources/DuckdbTableauSourcePanel';
import { IndexTable } from '@/components/admin/data-sources/IndexTable';
import { SyncRunningBadge, SyncStatusBadge } from '@/components/admin/data-sources/SyncStatusBadge';
import { TableTree } from '@/components/admin/data-sources/TableTree';
import { TableauResources } from '@/components/admin/data-sources/TableauResources';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { DescriptionSource, ServerJob, SyncRequest } from '@innermate/shared';
import { formatTableForLlm } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Pencil,
  PowerOff,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface AdminDataSourceDetailProps {
  id: string;
}

export function AdminDataSourceDetail({ id }: AdminDataSourceDetailProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState<'columns' | 'indexes'>('columns');

  const dataSourceQuery = useQuery({
    queryKey: ['admin', 'data-sources', id],
    queryFn: () => api.dataSourceGet(id),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return 5000;
      // tableau / duckdb 는 statsSyncedAt 가 영원히 null 이라 일반 분기로 가면 무한 3초 폴링.
      if (d.kind === 'tableau' || d.kind === 'duckdb') {
        return d.structureSyncedAt ? 30000 : 5000;
      }
      if (!d.structureSyncedAt || !d.statsSyncedAt) return 3000;
      return 15000;
    },
  });

  // tableau 데이터소스는 SQL 테이블/통계/jobs UI 가 의미 없음 — 무관 쿼리 비활성화로 분당 50+ 회 호출 절감.
  // duckdb 는 SQL 처럼 테이블 카탈로그 의미가 있으므로 그대로 활성화.
  const enableSqlQueries = !!dataSourceQuery.data && dataSourceQuery.data.kind !== 'tableau';

  const tablesQuery = useQuery({
    queryKey: ['admin', 'data-sources', id, 'tables'],
    queryFn: () => api.dataSourceTableList(id),
    refetchInterval: 5000,
    enabled: enableSqlQueries,
  });

  const tables = tablesQuery.data?.tables ?? [];

  useEffect(() => {
    if (!selectedTableId && tables.length > 0) {
      setSelectedTableId(tables[0]?.id ?? null);
    }
    if (selectedTableId && !tables.some((t) => t.id === selectedTableId)) {
      setSelectedTableId(tables[0]?.id ?? null);
    }
  }, [tables, selectedTableId]);

  const tableDetailQuery = useQuery({
    queryKey: ['admin', 'data-sources', id, 'tables', selectedTableId],
    queryFn: () => api.dataSourceTableGet(id, selectedTableId as string),
    enabled: !!selectedTableId && enableSqlQueries,
    refetchInterval: 5000,
  });

  const jobsQuery = useQuery({
    queryKey: ['admin', 'data-sources', id, 'jobs'],
    queryFn: () => api.dataSourceJobs(id),
    refetchInterval: 3000,
    enabled: enableSqlQueries,
  });
  const activeJobsByTask = useMemo(() => {
    const map = new Map<string, ServerJob>();
    for (const j of jobsQuery.data?.jobs ?? []) {
      // 실행 중 / 대기 / 재시도 대기 를 모두 "작업 중" 으로 취급.
      // permanently_failed / interrupted_by_restart 는 활성 아님.
      if (j.status === 'permanently_failed' || j.status === 'interrupted_by_restart') continue;
      const prev = map.get(j.taskIdentifier);
      if (!prev || (j.lockedAt && !prev.lockedAt)) map.set(j.taskIdentifier, j);
    }
    return map;
  }, [jobsQuery.data]);
  const structureJob = activeJobsByTask.get('data-source.sync-structure') ?? null;
  const statsJob = activeJobsByTask.get('data-source.sync-stats') ?? null;
  const descriptionsJob = activeJobsByTask.get('data-source.generate-descriptions') ?? null;
  const structureRunning = !!structureJob;
  const statsRunning = !!statsJob;
  const descriptionsRunning = !!descriptionsJob;

  const interruptedJobs = useMemo(
    () => (jobsQuery.data?.jobs ?? []).filter((j) => j.status === 'interrupted_by_restart'),
    [jobsQuery.data],
  );

  const syncMutation = useMutation({
    mutationFn: (body: SyncRequest) => api.dataSourceSync(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id, 'jobs'] });
    },
  });

  const cancelSyncMutation = useMutation({
    mutationFn: () => api.dataSourceSyncCancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id, 'jobs'] });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '취소 요청이 실패했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.dataSourceDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] });
      navigate({ to: '/admin/data-sources' });
    },
  });

  const tableDescriptionMutation = useMutation({
    mutationFn: (description: string | null) =>
      api.dataSourceUpdateTable(id, selectedTableId as string, { description }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'data-sources', id, 'tables', selectedTableId],
      });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id, 'tables'] });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '테이블 설명 저장에 실패했습니다.');
    },
  });

  const userScopeMutation = useMutation({
    mutationFn: (userScopeColumn: string | null) =>
      api.dataSourceSetTableUserScope(id, selectedTableId as string, { userScopeColumn }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'data-sources', id, 'tables', selectedTableId],
      });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : 'user scope 저장 실패');
    },
  });

  const generateDescMutation = useMutation({
    mutationFn: () => api.dataSourceGenerateDescriptions(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id] });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : 'LLM 설명 생성 요청 실패');
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: 테이블 전환 시 첫 탭으로 리셋이 핵심 의도
  useEffect(() => {
    setActiveTab('columns');
  }, [selectedTableId]);

  const ds = dataSourceQuery.data;

  if (dataSourceQuery.isLoading) {
    return (
      <PageShell>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (dataSourceQuery.error || !ds) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">
          {dataSourceQuery.error instanceof ApiError
            ? dataSourceQuery.error.message
            : '데이터소스를 찾을 수 없습니다.'}
        </p>
      </PageShell>
    );
  }

  const syncing = syncMutation.isPending;

  // Tableau 데이터소스: 자원 (workbook/datasource) 트리 + 통합 패널 분기.
  if (ds.kind === 'tableau') {
    return (
      <PageShell
        header={
          <>
            <BackLink />
            <PageHeader
              title={ds.name}
              description={`${ds.host} · ${
                ds.database === '_default' ? '기본 사이트' : `site=${ds.database}`
              }`}
              actions={
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => navigate({ to: '/admin/data-sources/$id/edit', params: { id } })}
                  >
                    <Pencil className="size-4" />
                    편집
                  </Button>
                </div>
              }
            />
          </>
        }
      >
        <TableauResources dataSourceId={id} />
      </PageShell>
    );
  }

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={ds.name}
            count={ds.tableCount}
            description={
              ds.kind === 'excel'
                ? 'Excel·CSV 데이터소스 — 시스템 DB 의 영구 스키마에 저장됩니다.'
                : ds.kind === 'duckdb'
                  ? '태블로 통합 산출물 (.duckdb 파일).'
                  : `${ds.host}:${ds.port}/${ds.database} · 사용자 ${ds.username}`
            }
            actions={
              <div className="flex items-center gap-2">
                {ds.kind === 'excel' ? (
                  <Button
                    onClick={() =>
                      navigate({
                        to: '/admin/data-sources/$id/append-excel',
                        params: { id },
                      })
                    }
                  >
                    <FileSpreadsheet className="size-4" />
                    엑셀 추가 업로드
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  onClick={() => navigate({ to: '/admin/data-sources/$id/edit', params: { id } })}
                >
                  <Pencil className="size-4" />
                  편집
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => syncMutation.mutate({ scope: 'structure' })}
                  disabled={syncing || structureRunning}
                  title={structureRunning ? '구조 동기화가 진행 중입니다.' : undefined}
                >
                  <RefreshCw className="size-4" />
                  구조 다시 sync
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => syncMutation.mutate({ scope: 'stats' })}
                  disabled={syncing || statsRunning}
                  title={
                    statsRunning
                      ? '통계 동기화가 진행 중입니다.'
                      : '통계가 없는 테이블만 처리합니다 (이어서). 전체를 다시 받으려면 옆의 "전체 다시" 사용.'
                  }
                >
                  <RefreshCw className="size-4" />
                  통계 다시 sync
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (
                      window.confirm(
                        '이미 통계가 수집된 테이블도 모두 처음부터 다시 처리합니다.\nbig table 분류도 초기화됩니다. 계속하시겠습니까?',
                      )
                    ) {
                      syncMutation.mutate({ scope: 'stats', fullReset: true });
                    }
                  }}
                  disabled={syncing || statsRunning}
                  title="statsSyncedAt / isBigTable 등을 초기화하고 처음부터 통계 수집"
                >
                  통계 전체 다시
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => generateDescMutation.mutate()}
                  disabled={generateDescMutation.isPending || descriptionsRunning}
                  title={
                    descriptionsRunning
                      ? 'LLM 설명 생성이 진행 중입니다.'
                      : 'comment 도 description 도 없는 테이블/컬럼에 대해 LLM 이 설명을 유추합니다.'
                  }
                >
                  <Sparkles className="size-4" />
                  설명 재생성
                </Button>
                {structureRunning || statsRunning || descriptionsRunning ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm('진행 중인 동기화 작업을 모두 취소하시겠습니까?')) {
                        cancelSyncMutation.mutate();
                      }
                    }}
                    disabled={cancelSyncMutation.isPending}
                    title="진행 중인 워커가 다음 항목 처리 전에 중단합니다."
                  >
                    <X className="size-4" />
                    동기화 취소
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(`"${ds.name}" 를 삭제하시겠습니까?`)) {
                      deleteMutation.mutate();
                    }
                  }}
                  aria-label="삭제"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            }
          />
        </>
      }
    >
      {interruptedJobs.length > 0 ? (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-[hsl(45_85%_50%)]/30 bg-[hsl(45_85%_50%)]/5 px-3 py-2 text-xs text-[hsl(40_85%_40%)]">
          <PowerOff className="size-3.5" />
          <span>
            서버 재시작으로 중단된 작업이 {interruptedJobs.length}개 있습니다. "다시 sync" 버튼을
            다시 누르면 끊긴 지점부터 재개합니다.
          </span>
        </div>
      ) : null}
      {ds.kind === 'duckdb' ? (
        <div className="mb-4">
          <DuckdbTableauSourcePanel
            duckdbDataSourceId={id}
            onDeleted={() => navigate({ to: '/admin/data-sources' })}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {structureRunning ? (
          <span className="inline-flex items-center gap-2">
            <SyncRunningBadge label="구조" />
            {progressLabel(structureJob)}
          </span>
        ) : (
          <SyncStatusBadge label="구조" syncedAt={ds.structureSyncedAt} error={ds.lastSyncError} />
        )}
        {statsRunning ? (
          <span className="inline-flex items-center gap-2">
            <SyncRunningBadge label="통계" />
            {progressLabel(statsJob)}
          </span>
        ) : (
          <SyncStatusBadge label="통계" syncedAt={ds.statsSyncedAt} />
        )}
        {descriptionsRunning ? (
          <span className="inline-flex items-center gap-2">
            <SyncRunningBadge label="설명" />
            {progressLabel(descriptionsJob)}
          </span>
        ) : (
          <SyncStatusBadge label="설명" syncedAt={ds.descriptionsGeneratedAt} />
        )}
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 h-6 text-[11px] text-foreground-muted hover:text-foreground"
        >
          {showPreview ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          Skill 미리보기
        </button>
      </div>

      {ds.description ? (
        <p className="mb-6 text-sm text-foreground-muted">{ds.description}</p>
      ) : null}

      <div className="grid grid-cols-[280px_1fr] gap-6 min-h-[480px]">
        <div className="border border-border rounded-2xl bg-surface overflow-hidden">
          {tablesQuery.isLoading ? (
            <p className="text-xs text-foreground-muted p-4">테이블 로딩 중…</p>
          ) : (
            <TableTree
              dataSourceId={id}
              tables={tables}
              selectedId={selectedTableId}
              onSelect={setSelectedTableId}
            />
          )}
        </div>

        <div className="min-w-0 space-y-4">
          {tableDetailQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">테이블 상세 로딩 중…</p>
          ) : !tableDetailQuery.data ? (
            <EmptyTableState />
          ) : (
            <>
              <div className="rounded-2xl border border-border bg-surface p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <h2 className="group inline-flex items-center gap-2 text-lg font-semibold">
                      <span>
                        <span className="text-foreground-muted font-mono text-base">
                          {tableDetailQuery.data.table.schemaName}.
                        </span>
                        {tableDetailQuery.data.table.tableName}
                        {tableDetailQuery.data.table.isView ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-foreground-muted">
                            (뷰)
                          </span>
                        ) : null}
                      </span>
                      <CopyButton
                        text={`${tableDetailQuery.data.table.schemaName}.${tableDetailQuery.data.table.tableName}`}
                        label={`${tableDetailQuery.data.table.schemaName}.${tableDetailQuery.data.table.tableName} 복사`}
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                      />
                    </h2>
                    <p className="mt-1 text-xs text-foreground-muted">
                      {tableDetailQuery.data.columns.length}개 컬럼
                      {tableDetailQuery.data.table.estimatedRowCount !== null
                        ? ` · 약 ${tableDetailQuery.data.table.estimatedRowCount.toLocaleString('ko-KR')}행`
                        : ''}
                    </p>
                  </div>
                </div>
                <DescriptionEditor
                  value={tableDetailQuery.data.table.description}
                  placeholder="+ 테이블 설명 추가 (LLM 컨텍스트에 포함됩니다)"
                  onSave={(next) => tableDescriptionMutation.mutateAsync(next)}
                  saving={tableDescriptionMutation.isPending}
                />
                <DescriptionSourceBadge source={tableDetailQuery.data.table.descriptionSource} />

                <UserScopeColumnEditor
                  current={tableDetailQuery.data.table.userScopeColumn}
                  columns={tableDetailQuery.data.columns.map((c) => c.columnName)}
                  onSave={(next) => userScopeMutation.mutateAsync(next)}
                  saving={userScopeMutation.isPending}
                />

                <ScopeRulesEditor
                  dataSourceId={id}
                  tableId={selectedTableId as string}
                  tableColumns={tableDetailQuery.data.columns
                    .filter((c) => !c.removedAt)
                    .map((c) => c.columnName)}
                />
              </div>

              {showPreview ? <SkillPreview detail={tableDetailQuery.data} /> : null}

              <div>
                <div className="mb-3 flex items-center gap-1">
                  <TabButton
                    active={activeTab === 'columns'}
                    onClick={() => setActiveTab('columns')}
                  >
                    컬럼 ({tableDetailQuery.data.columns.filter((c) => !c.removedAt).length})
                  </TabButton>
                  <TabButton
                    active={activeTab === 'indexes'}
                    onClick={() => setActiveTab('indexes')}
                  >
                    인덱스 ({tableDetailQuery.data.indexes.filter((i) => !i.removedAt).length})
                  </TabButton>
                </div>
                {activeTab === 'columns' ? (
                  <ColumnTable dataSourceId={id} detail={tableDetailQuery.data} />
                ) : (
                  <IndexTable indexes={tableDetailQuery.data.indexes} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}

function progressLabel(job: ServerJob | null): React.ReactNode {
  if (!job || job.progressTotal === null || job.progressTotal === 0) return null;
  const done = job.progressDone ?? 0;
  const pct = Math.round((done / job.progressTotal) * 100);
  const main = `${done}/${job.progressTotal} (${pct}%)`;
  return (
    <span className="text-[11px] text-foreground-muted" title={job.progressItem ?? undefined}>
      {main}
      {job.progressItem ? ` · ${job.progressItem}` : ''}
    </span>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center rounded-md px-3 text-xs font-medium transition-colors',
        active
          ? 'bg-accent/15 text-accent'
          : 'text-foreground-muted hover:text-foreground hover:bg-surface-muted/40',
      )}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function DescriptionSourceBadge({ source }: { source: DescriptionSource | null }) {
  if (source === null || source === 'manual') return null;
  const label = source === 'comment' ? 'DB 코멘트 원본' : 'LLM 유추';
  const tone = source === 'llm' ? 'accent' : 'muted';
  return (
    <div className="mt-2">
      <span
        className={cn(
          'inline-flex items-center rounded-sm px-1.5 h-5 text-[10px] uppercase tracking-wider',
          tone === 'accent' && 'bg-accent/15 text-accent',
          tone === 'muted' && 'bg-surface-muted/60 text-foreground-muted',
        )}
      >
        {label}
      </span>
    </div>
  );
}

function UserScopeColumnEditor({
  current,
  columns,
  onSave,
  saving,
}: {
  current: string | null;
  columns: string[];
  onSave: (next: string | null) => undefined | Promise<unknown>;
  saving: boolean;
}) {
  const value = current ?? '';
  return (
    <div className="mt-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-foreground">
            사용자 범위 컬럼 (user_scope_column)
          </div>
          <div className="text-xs text-foreground-muted mt-0.5">
            설정하면 이 데이터소스의 관리자 권한이 없는 사용자는 반드시 이 컬럼 = 본인 식별자 로
            필터해야 조회 가능합니다.
          </div>
        </div>
        <select
          value={value}
          onChange={(e) => {
            const next = e.target.value === '' ? null : e.target.value;
            void onSave(next);
          }}
          disabled={saving}
          className="h-8 rounded border border-border bg-surface px-2 text-xs"
        >
          <option value="">(공용 — 필터 없음)</option>
          {columns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function BackLink() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/admin/data-sources' })}
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground self-start"
    >
      <ArrowLeft className="size-3.5" />
      데이터 소스 목록
    </button>
  );
}

function EmptyTableState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-6 py-12 text-center">
      <p className="text-sm text-foreground-muted">
        구조 sync 가 완료되면 테이블 목록이 표시됩니다.
      </p>
      <p className="mt-1 text-xs text-foreground-muted">
        첫 sync 가 진행 중인 경우 잠시 후 자동으로 갱신됩니다.
      </p>
    </div>
  );
}

function SkillPreview({
  detail,
}: {
  detail: {
    table: Parameters<typeof formatTableForLlm>[0];
    columns: Parameters<typeof formatTableForLlm>[1];
    indexes: NonNullable<Parameters<typeof formatTableForLlm>[2]>;
  };
}) {
  const text = useMemo(
    () => formatTableForLlm(detail.table, detail.columns, detail.indexes),
    [detail],
  );
  return (
    <div className="rounded-2xl border border-border bg-surface-muted/30 p-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex items-center rounded-sm bg-accent/15 text-accent px-1.5 h-5 text-[10px] uppercase tracking-wider">
          LLM 컨텍스트 미리보기
        </span>
        <span className="text-xs text-foreground-muted">
          Phase 9 의 db.describe_table 툴이 LLM 에 전달할 내용입니다.
        </span>
      </div>
      <pre className="whitespace-pre-wrap font-mono text-xs text-foreground leading-relaxed max-h-[320px] overflow-y-auto">
        {text}
      </pre>
    </div>
  );
}
